import { ReactElement, useCallback } from 'react';
import { AxiosResultDefaultError } from '../../../../api/request';
import { createFetchContext } from '../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../components/info/DisplayError';
import { getRequestsResolved } from '../api';
import { StudentsRemovalRequestsResolvedResponse } from '../types';

export const ITEMS_PER_PAGE_DEFAULT = 10;

export interface StudentsRemovalRequestsResolvedQuery {
  page: number;
  studentNameOrId?: string;
  studyPlanVersionId?: number;
  requestState?: string;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  StudentsRemovalRequestsResolvedQuery,
  StudentsRemovalRequestsResolvedResponse,
  AxiosResultDefaultError
>();

export const useStudentsRemovalRequestsResolvedList = useFetch;

export function StudentsRemovalRequestsResolvedListConsumer({
  children,
}: {
  children: (args: {
    data?: StudentsRemovalRequestsResolvedResponse;
    loading: boolean;
    refresh: () => Promise<void>;
  }) => ReactElement;
}) {
  return (
    <FetchConsumer>
      {({ data, error, loading, refresh }) => {
        if (error) {
          return (
            <DisplayError
              insideCard
              textBody={error.code}
              retryAction={refresh}
              loadingAction={loading}
            />
          );
        }

        return children({ data, loading, refresh });
      }}
    </FetchConsumer>
  );
}

export function StudentsRemovalRequestsResolvedListLoader({
  children,
}: {
  children: ReactElement | ReactElement[];
}) {
  const request = useCallback(
    async (query: StudentsRemovalRequestsResolvedQuery) => {
      return getRequestsResolved({
        ...query,
        studentNameOrId: query.studentNameOrId || undefined,
        studyPlanVersionId: query.studyPlanVersionId,
        requestState: query.requestState,
        page: query.page - 1,
      });
    },
    [],
  );

  return (
    <FetchProvider
      request={request}
      defaultQuery={{ page: 1 }}
      children={children}
      fetchImmediately
    />
  );
}
